import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { firstValueFrom, Subscription } from 'rxjs';
import { PriceListEnums } from 'src/app/enum/price-list-enums';
import { PriceListDetailModel } from 'src/app/models/archive/price-list-detail.model';
import { PriceListModel } from 'src/app/models/archive/price-list.model';
import { ArticleService } from 'src/app/services/archive/article.service';
import { PriceListDetailService } from 'src/app/services/archive/price-list-detail.service';
import { ConfirmDialogService } from 'src/app/services/confirm-dialog/confirm-dialog.service';
import { FormControlStateService } from 'src/app/services/global-services/form-control-state.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';

@Component({
  selector: 'app-register-price-list-detail',
  templateUrl: './register-price-list-detail.component.html',
  styleUrls: ['./register-price-list-detail.component.scss'],
  standalone: false
})
export class RegisterPriceListDetailComponent implements OnChanges {

  @Input() permissionUpdatable: boolean = false;
  @Input() priceListDetailContract: PriceListDetailModel = new PriceListDetailModel();
  @Input() priceListModel: PriceListModel = new PriceListModel();
  @Output() isFormView = new EventEmitter<boolean>();
  @Output() updateListDetail = new EventEmitter<boolean>(false);

  articleId: number = 0;
  codeNameArticle: string = '';
  formPriceListDetail: FormGroup = new FormGroup({});
  priceListDetailId: number = 0;
  subscription: Subscription;

  constructor(private articleService: ArticleService,
    private confirmDialogService: ConfirmDialogService,
    private formBuilder: FormBuilder,
    private formControlStateService: FormControlStateService,
    private priceListDetailService: PriceListDetailService,
    private toastHandlerService: ToastHandlerService) {
    this.validateForm(this.priceListDetailContract);
  }

  async ngOnChanges(changes: SimpleChanges) {
    const priceListDetailContract = changes["priceListDetailContract"];
    if (this.priceListDetailContract.priceListDetailId === 0 && priceListDetailContract) {
      this.createNewPriceListDetail();
    } else {
      this.priceListDetailId = this.priceListDetailContract.priceListDetailId;
      await this.getArticle(this.priceListDetailContract.articleId);
      this.validateForm(this.priceListDetailContract);
    }
  }

  changeBaseUnitValue(baseUnitValue: number) {
    this.formPriceListDetail.controls["clientUnitValue"].setValue(baseUnitValue);
    this.formPriceListDetail.controls["rateDiscount"].setValue(0);
  }

  changeRateDiscount(rateDiscount: number) {
    const clientUnitValue = this.formPriceListDetail.controls["baseUnitValue"].value -
      (this.formPriceListDetail.controls["baseUnitValue"].value * rateDiscount / 100);
    this.formPriceListDetail.controls["clientUnitValue"].setValue(clientUnitValue);
  }

  async changeSwManual(swManual: boolean) {
    if (!this.permissionUpdatable) {
      this.toastHandlerService.showToast(
        'error',
        'El usuario no tiene permisos para modificar listas de precios. Comuníquese con el administrador del sistema.'
      );
      this.formPriceListDetail.controls["swManual"].setValue(!swManual);
      return;
    }
    this.updateFormState(swManual);
    if (swManual) {
      this.formPriceListDetail.controls["baseUnitValue"].setValue(0);
      this.formPriceListDetail.controls["rateDiscount"].setValue(0);
    } else {
      try {
        const response = await firstValueFrom(this.priceListDetailService.getBaseUnitValue(this.priceListModel.branchId, this.articleId));
        this.formPriceListDetail.controls["baseUnitValue"].setValue(response);
        //Válido si la lista tiene tarifa descuento para calcular el valor cliente
        if (this.priceListModel.rateDiscount != 0) {
          this.formPriceListDetail.controls["rateDiscount"].setValue(this.priceListModel.rateDiscount);
          this.changeRateDiscount(this.formPriceListDetail.controls["rateDiscount"].value);
        } else {
          this.formPriceListDetail.controls["clientUnitValue"].setValue(response);
        }
      } catch (error) {
        console.error('Error al obtener el valor base.', error);
      }
    }
  }

  clearForm() {
    this.codeNameArticle = '';
    this.priceListDetailContract = new PriceListDetailModel();
    this.priceListDetailId = 0;
    this.articleId = 0;
    this.validateForm(this.priceListDetailContract);
    this.isFormView.emit(false);
  }

  createNewPriceListDetail() {
    this.codeNameArticle = '';
    this.articleId = 0;
    this.priceListDetailId = 0;
    this.validateForm(this.priceListDetailContract);
  }

  async getArticle(articleId: number | null) {
    if (articleId != null && articleId !== 0) {
      const article = await firstValueFrom(this.articleService.getArticleById(articleId));
      if (article) {
        this.articleId = articleId;
        this.codeNameArticle = `${article.code} - ${article.name}`;
      } else {
        this.articleId = 0;
        this.codeNameArticle = "Error al buscar el elemento. Por favor refresque la página.";
        return;
      }
    } else {
      this.articleId = 0;
      this.codeNameArticle = "";
    }
  }

  async send() {
    if (!this.permissionUpdatable) {
      this.toastHandlerService.showToast(
        'error',
        'El usuario no tiene permisos para modificar listas de precios. Comuníquese con el administrador del sistema.');
      return;
    }
    Object.keys(this.formPriceListDetail.controls).forEach(key => {
      const control = this.formPriceListDetail.get(key);
      if (control && control.invalid) {
        console.warn(`${key} es inválido`, control.errors, 'value:', control.value);
      }
    });
    if (this.formPriceListDetail.valid) {
      const modelPriceListDetail = this.formPriceListDetail.getRawValue() as PriceListDetailModel;
      modelPriceListDetail.priceListId = this.priceListModel.priceListId;
      modelPriceListDetail.articleId = this.articleId;

      try {
        if (this.priceListDetailId == 0) {
          try {
            this.priceListDetailContract = await firstValueFrom(this.priceListDetailService.createPriceListDetail(modelPriceListDetail));
            this.toastHandlerService.showToast(
              'success',
              'El detalle de la salida fue creado exitosamente.'
            );
            this.updateListDetail.emit(true);
          } catch (error) {
            console.error('Error al crear el detalle de la salida.', error);
          }
        } else {
          this.priceListDetailContract.baseUnitValue = modelPriceListDetail.baseUnitValue;
          this.priceListDetailContract.rateDiscount = modelPriceListDetail.rateDiscount;
          this.priceListDetailContract.clientUnitValue = modelPriceListDetail.clientUnitValue;
          this.priceListDetailContract.swManual = modelPriceListDetail.swManual;
          try {
            await firstValueFrom(this.priceListDetailService.updatePriceListDetail(this.priceListDetailContract));
            this.toastHandlerService.showToast(
              'success',
              'El detalle de la salida fue modificado exitosamente.'
            );
            this.updateListDetail.emit(true);
          } catch (error) {
            this.updateListDetail.emit(true);
            console.error('Error al modificar el detalle de la salida.', error);
          }
        }
        if (this.priceListDetailContract.replicateInOtherlist) {
          if (await this.confirmDialogService.confirm(
            "Desea replicar este artículo en las demás listas de precios? El valor definido aquí se asignará como base en las demás listas."
          )) {
            try {
              await firstValueFrom(this.priceListDetailService.replicateArticleInOtherList(this.priceListDetailContract));
              this.toastHandlerService.showToast(
                'success',
                'El artículo fue replicado exitosamente.'
              );
              this.updateListDetail.emit(true);
            } catch (error) {
              this.toastHandlerService.showToast(
                'success',
                'El al replicar el artículo en las demás listas.'
              );
              console.error('Error al replicar el artículo en las demás listas.', error);
              this.updateListDetail.emit(true);
            }
          }
        }
        this.createNewPriceListDetail()
        this.isFormView.emit(false);
      } catch (error) {
        console.error('Error al obtener las existencias por artículo y bodega.', error);
      }
    } else {
      this.showValidationMessages();
    }
  }

  showValidationMessages() {
    const controlMessages: Record<string, Record<string, string>> = {
      storeId: {
        required: 'El campo Bodega es obligatorio.',
        min: 'Debe seleccionar una Bodega válida.'
      },
      articleId: {
        required: 'El campo Artículo es obligatorio.',
        min: 'Debe seleccionar un Artículo válido.'
      },
      quantity: {
        required: 'El campo Cantidad es obligatorio.',
        min: 'Debe ingresar una Cantidad igual o mayor a 1.'
      }
    };

    Object.keys(controlMessages).forEach((control) => {
      const formControl = this.formPriceListDetail.get(control);
      if (formControl && formControl.invalid && formControl.errors) {
        const messages = controlMessages[control];
        Object.keys(formControl.errors).forEach((errorKey) => {
          const message = messages[errorKey];
          if (message) {
            this.toastHandlerService.showToast('warning', message);
          }
        });
      }
    });
  }

  updateFormState(swManual: boolean) {
    if (this.priceListModel.code === PriceListEnums.base) {
      setTimeout(() => this.formControlStateService.setControlState(
        this.formPriceListDetail, ['rateDiscount', 'clientUnitValue', 'swManual'], false
      ), 100);
      setTimeout(() => this.formPriceListDetail.controls['baseUnitValue'].enable(), 100);
    } else {
      if (swManual) {
        setTimeout(() => this.formControlStateService.setControlState(
          this.formPriceListDetail, ['baseUnitValue', 'rateDiscount'], false
        ), 100);
        setTimeout(() => this.formPriceListDetail.controls['clientUnitValue'].enable(), 100);
      } else {
        setTimeout(() => this.formControlStateService.setControlState(
          this.formPriceListDetail, ['baseUnitValue', 'clientUnitValue'], false
        ), 100);
        setTimeout(() => this.formPriceListDetail.controls['rateDiscount'].enable(), 100);
      }
      setTimeout(() => this.formPriceListDetail.controls['swManual'].enable(), 100);
    }
  }

  validateForm(priceListDetail: PriceListDetailModel) {
    this.formPriceListDetail = this.formBuilder.group({
      articleId: [priceListDetail.articleId, [Validators.required, Validators.min(1)]],
      baseUnitValue: [priceListDetail.baseUnitValue, [Validators.required, Validators.min(0)]],
      rateDiscount: [priceListDetail.rateDiscount, [Validators.required]],
      clientUnitValue: [priceListDetail.clientUnitValue, [Validators.required, Validators.min(0)]],
      swManual: [priceListDetail.swManual]
    });
    if (priceListDetail.articleId !== 0) {
      this.getArticle(priceListDetail.articleId);
    }
    this.updateFormState(priceListDetail.swManual);
  }

}
